import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'ha/i18n';
import { Button, Link } from '@hbf/dsl';

import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';

import {
  getCity,
  getListingId,
  getAdvertiser,
  getIsMultiUnit,
  getUnitTypeUnits,
} from '../selectors';

interface Props {
  label?: string;
}

const useStyles = makeStyles()(() => ({
  button: {
    width: '100%',
    display: 'block',
    marginBottom: '1rem',
  },
}));

const ListingLinkToPartner: React.FC<Props> = ({ label }) => {
  const { T, urlResolver } = useIntl();
  const { classes } = useStyles();

  const { isUnitTypeId } = useUnitTypeId();

  const city = useSelector(getCity);
  const listingId = useSelector(getListingId);
  const currentUserId = useSelector(getUserId);
  const advertiser = useSelector(getAdvertiser);
  const isMultiUnit = useSelector(getIsMultiUnit);
  const units = useSelector(getUnitTypeUnits);

  const analyticsEventProps = React.useMemo(() => {
    return isUnitTypeId
      ? {
          unitId: isMultiUnit ? undefined : units?.[0]?.id,
          unitTypeId: listingId,
          isMultiUnit,
        }
      : {};
  }, [isUnitTypeId, isMultiUnit, listingId, units]);

  const roomId = React.useMemo(() => {
    if (isUnitTypeId) {
      return isMultiUnit ? undefined : units?.[0]?.id;
    }
    return listingId;
  }, [isUnitTypeId, isMultiUnit, listingId, units]);

  return (
    <Track
      name="Clicked View room on partner website"
      type="button"
      city={city}
      userID={currentUserId}
      roomID={roomId}
      getDynamicData={() => analyticsEventProps}
    >
      <Link
        to={urlResolver.getPartnerRedirect({
          roomid: isUnitTypeId ? units?.[0]?.id : listingId,
          advertiserid: advertiser.id,
        })}
        target="_blank"
        rel="nofollow"
        data-test-locator="Listing/Link to Partner"
      >
        <Button color="primary" variant="contained" className={classes.button}>
          {label || T('View room on partner website')}
        </Button>
      </Link>
    </Track>
  );
};

export { ListingLinkToPartner };
